import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { XIcon, HelpCircleIcon, ChevronRightIcon } from 'lucide-react';
import { RichText } from './RichText.js';
import { useAuth } from '../auth/AuthProvider.js';
import { PanelLegenda } from './PanelLegenda.js';
import type { WelcomePanelResponse } from '@kennisbank/shared';
import { ApiError, apiGet, apiPost } from '../api/client.js';
import { useTenantBrand } from '../lib/tenant-brand.js';

/**
 * Welkomstpaneel — Linda feedback 2026-05-20.
 *
 * Three surfaces, one source of truth:
 *  - Modal that auto-fires on the user's first visit and re-fires
 *    whenever Linda bumps the version (suppohrt_admin saves new content).
 *    Mounted app-wide from Layout ('modal-only'): de startpagina is Nieuws,
 *    niet Mila, dus daar alleen zou het paneel de meeste mensen missen.
 *  - Dismissible banner that lives at the top of the Mila page
 *    until the user clicks "X" or "Begrepen" inside the modal.
 *  - /help page that always renders the latest content full-width.
 *
 * Trigger logic: modal shows when `version > lastSeenVersion`. Dismiss
 * POSTs the current version so subsequent loads don't re-fire (until
 * the next admin save bumps version again). In de demo gaat dat wegklikken
 * naar localStorage in plaats van naar de server (zie leesDemoGezienVersie).
 *
 * Empty state: when no content has been written yet, the modal stays
 * hidden and the banner shows a gentle suggestion to suppohrt_admin
 * users only ("Beheer dit paneel via /beheer/welkomstpaneel").
 */

/** Accounts zonder Mila en Sara krijgen de eigen tekst; is die leeg, dan de
 * gewone. Zo blijft een paneel dat nog maar één tekst heeft gewoon werken. */
function tekstVoor(data: WelcomePanelResponse, aiTools: boolean | undefined): string {
  if (aiTools) return data.content;
  return data.contentZonderAi.trim() ? data.contentZonderAi : data.content;
}

const DEMO_GEZIEN_KEY = 'kennisbank.welkomstpaneel.demoGezienVersie';

/**
 * In de demo delen alle bezoekers één account, dus mag het wegklikken niet naar
 * de server: de eerste prospect die "Begrepen" klikt zou de uitleg voor alle
 * volgende verbergen, en de nachtelijke demo-reset zou de voorkeur toch weer
 * overschrijven. Per browser onthouden is hier de juiste korrel. De versie gaat
 * mee zodat een nieuwe paneeltekst de modal wel opnieuw laat zien.
 */
export function leesDemoGezienVersie(): number {
  try {
    const raw = localStorage.getItem(DEMO_GEZIEN_KEY);
    const versie = Number(raw);
    return Number.isFinite(versie) ? versie : 0;
  } catch {
    // Geblokkeerde site-data mag de pagina niet stukmaken.
    return 0;
  }
}

export function schrijfDemoGezienVersie(versie: number): void {
  try {
    localStorage.setItem(DEMO_GEZIEN_KEY, String(versie));
  } catch {
    // Zie leesDemoGezienVersie: dan komt de modal bij de volgende lading terug.
  }
}

interface WelcomePanelPropsBase {
  /** 'auto' = banner + modal, 'banner-only' = alleen de banner,
   * 'modal-only' = alleen de modal (app-breed vanuit Layout),
   * 'full-page' = /help.
   * Banner is hidden after dismissal until version bumps. */
  variant?: 'auto' | 'banner-only' | 'modal-only' | 'full-page';
}

// Eén fetch per sessie: Layout rendert dit paneel op elke pagina, dus zonder
// cache ging er bij elke navigatie opnieuw een request uit. Na dismiss of
// opslaan in Beheer vervalt de cache, zodat de versiecheck weer klopt.
let welcomeCache: Promise<WelcomePanelResponse> | null = null;
function laadWelcomePanel(): Promise<WelcomePanelResponse> {
  welcomeCache ??= apiGet<WelcomePanelResponse>('/api/v1/welcome-panel').catch((err: unknown) => {
    welcomeCache = null;
    throw err;
  });
  return welcomeCache;
}
export function vergeetWelcomePanel(): void {
  welcomeCache = null;
}

export function WelcomePanel({ variant = 'auto' }: WelcomePanelPropsBase): JSX.Element | null {
  const { user } = useAuth();
  const { beheerder } = useTenantBrand();
  const demo = user?.demo === true;
  const [data, setData] = useState<WelcomePanelResponse | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await laadWelcomePanel();
        if (cancelled) return;
        setData(resp);
        // Modal fires when content exists AND user hasn't seen this version.
        // Welke van de twee teksten deze gebruiker krijgt beslist de render;
        // hier is alleen "is er überhaupt tekst" relevant, zodat de fetch niet
        // aan `user` hoeft te hangen.
        const gezien = demo
          ? Math.max(resp.lastSeenVersion, leesDemoGezienVersie())
          : resp.lastSeenVersion;
        if ((resp.content.trim() || resp.contentZonderAi.trim()) && resp.version > gezien) {
          setShowModal(true);
        }
      } catch (err) {
        // Non-fatal: missing/unreachable welcome panel must not break the app.
        if (!(err instanceof ApiError)) {
          console.warn('welcome-panel fetch failed', err);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [demo]);

  async function dismiss() {
    setShowModal(false);
    setDismissed(true);
    if (!data) return;
    if (demo) {
      schrijfDemoGezienVersie(data.version);
      return;
    }
    try {
      await apiPost('/api/v1/welcome-panel/dismiss', { version: data.version });
      vergeetWelcomePanel();
    } catch (err) {
      // Dismissal is best-effort; the modal stays hidden for this
      // session even if the POST fails.
      console.warn('welcome-panel dismiss failed', err);
    }
  }

  if (!loaded || !data) return null;

  const tekst = tekstVoor(data, user?.aiTools);

  if (variant === 'full-page') {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-8">
        <div className="mb-4 flex items-center gap-2 text-xs uppercase tracking-wider text-ink-900/60">
          <HelpCircleIcon size={14} />
          <span>Welkomstpaneel</span>
        </div>
        {tekst.trim() ? (
          <>
            <RichText value={tekst} className="text-sm text-ink-900/80" />
            {user?.aiTools && <PanelLegenda />}
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-ring2 bg-white px-6 py-10 text-center text-sm text-ink-900/55">
            Er is nog geen welkomsttekst ingesteld. {beheerder} kan dit paneel bewerken via{' '}
            <Link to="/beheer/welkomstpaneel" className="text-brand-700 hover:underline">
              Beheer Welkomstpaneel
            </Link>
            .
          </div>
        )}
      </div>
    );
  }

  // Banner + modal variants
  const hasContent = tekst.trim().length > 0;

  return (
    <>
      {variant !== 'banner-only' && showModal && hasContent && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcome-panel-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={dismiss}
        >
          <div
            className="relative max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-ring2 px-6 py-4">
              <div>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-brand-700">
                  <HelpCircleIcon size={13} />
                  <span>Welkom bij de Kennishub</span>
                </div>
                <h2 id="welcome-panel-title" className="mt-1 text-lg font-bold text-ink-900">
                  Belangrijk om te weten
                </h2>
              </div>
              <button
                type="button"
                onClick={dismiss}
                className="rounded-full p-1 text-ink-900/55 hover:bg-ring2/40 hover:text-ink-900"
                aria-label="Sluiten"
              >
                <XIcon size={18} />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
              <RichText value={tekst} className="text-sm text-ink-900/80" />
              {user?.aiTools && <PanelLegenda />}
            </div>
            <div className="flex items-center justify-end border-t border-ring2 px-6 py-3">
              <button
                type="button"
                onClick={dismiss}
                className="rounded-full bg-brand-700 px-5 py-1.5 text-sm font-semibold text-white hover:bg-brand-800"
              >
                Begrepen
              </button>
            </div>
          </div>
        </div>
      )}
      {variant !== 'modal-only' && hasContent && !dismissed && (
        <Link
          to="/help"
          className="mb-3 flex items-center justify-between gap-2 rounded-xl border border-ring2 bg-ring1-bg/40 px-4 py-2.5 text-sm text-ink-900 hover:border-brand-700"
        >
          <span className="flex items-center gap-2">
            <HelpCircleIcon size={14} className="shrink-0 text-brand-700" />
            <span>
              <span className="font-semibold">Welkomstpaneel</span>
              <span className="text-ink-900/60">
                {' '}
                · klik om de instructies en disclaimer te lezen
              </span>
            </span>
          </span>
          <ChevronRightIcon size={14} className="shrink-0 text-ink-900/40" />
        </Link>
      )}
    </>
  );
}
